import React from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CategoryIcon from '@mui/icons-material/Category';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  Box,
  Grid,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import LabelChip from 'components/chip/LabelChip';
import {
  EmptyHint,
  PagedWidgetList,
  StatTile,
  Widget,
  WidgetDonutChart,
  WidgetRow,
} from 'components/dashboard/DashboardWidgets';
import DomainHero, { HERO_PALETTES } from 'components/dashboard/DomainHero';
import { useDashboardProfile } from 'pages/useDashboardProfile';
import {
  ExpenseAggregate,
  ExpenseListItem,
  aggregateExpense,
  listExpenses,
} from 'stores/ExpenseStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { clampPage, mergeWithDefaults, resolveOption } from 'utils/UserProfileGuard';

import ExpensePeriodSelector, {
  DEFAULT_DATE_RANGE,
  ExpenseDateRange,
  PERIOD_PRESET_KEYS,
  resolveDateRange,
} from './components/ExpensePeriodSelector';

const ACCENT = '#ef6c00';
const RECENT_COUNT = 5;

type HomeState = {
  dateRange: ExpenseDateRange;
  recentPage: number;
};

const DEFAULTS: HomeState = {
  dateRange: DEFAULT_DATE_RANGE,
  recentPage: 0,
};

const yen = (n: number) => `${n.toLocaleString('ja-JP')} 円`;

/**
 * 経費Home（簡易ダッシュボード）。
 * ヒーロー → 期間セレクタ(共通) → KPI(合計金額/件数) → 最近の経費 → 品目ラベル別構成比。
 * 「もっと詳しく」で詳細ダッシュボードへ。期間は user_profile('expense-dashboard') で Dashboard と共有。
 */
const ExpenseHome: React.FC = () => {
  const navigate = useNavigate();

  // マスタ設定（品目ラベル/店舗）へのショートカット（契約ドメインと同じヒーロー内メニュー）
  const [settingsAnchor, setSettingsAnchor] = React.useState<HTMLElement | null>(null);
  const settingsItems = [
    {
      key: 'label',
      label: '品目ラベル管理',
      icon: <CategoryIcon fontSize="small" />,
      to: '/expense/expense-label',
    },
    {
      key: 'store',
      label: '店舗管理',
      icon: <StorefrontIcon fontSize="small" />,
      to: '/expense/expense-store',
    },
  ];

  const [rawState, setState, restored] = useDashboardProfile<HomeState>(
    'expense-dashboard',
    DEFAULTS,
  );

  // 復元ガード（dateRange.preset を妥当な選択肢へ）
  const state = React.useMemo<HomeState>(() => {
    const merged = mergeWithDefaults(DEFAULTS, rawState);
    return {
      dateRange: {
        ...merged.dateRange,
        preset: resolveOption(merged.dateRange.preset, PERIOD_PRESET_KEYS, 'THIS_MONTH'),
      },
      recentPage: merged.recentPage,
    };
  }, [rawState]);

  const [aggregate, setAggregate] = React.useState<ExpenseAggregate | null>(null);
  const [recent, setRecent] = React.useState<ExpenseListItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  const period = React.useMemo(() => resolveDateRange(state.dateRange), [state.dateRange]);

  const setDateRange = (dateRange: ExpenseDateRange) =>
    setState((prev) => ({ ...prev, dateRange }));
  const setRecentPage = React.useCallback(
    (p: number) => setState((prev) => ({ ...prev, recentPage: p })),
    [setState],
  );

  const dateParams = React.useMemo(
    () => ({
      ...(period.dateFrom ? { dateFrom: period.dateFrom } : {}),
      ...(period.dateTo ? { dateTo: period.dateTo } : {}),
    }),
    [period.dateFrom, period.dateTo],
  );

  const reload = React.useCallback(async () => {
    const [agg, list] = await Promise.all([
      aggregateExpense({ axis: 'LABEL', ...dateParams }).catch((): ExpenseAggregate | null => null),
      listExpenses(dateParams).catch((): ExpenseListItem[] => []),
    ]);
    setAggregate(agg);
    setRecent(list);
  }, [dateParams]);

  React.useEffect(() => {
    if (!restored) return;
    let active = true;
    setLoading(true);
    reload().finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [restored, reload]);

  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await reload();
    } finally {
      setRefreshing(false);
    }
  }, [reload]);

  const totalAmount = aggregate?.totalAmount ?? 0;
  const totalCount = aggregate?.totalCount ?? 0;

  // 最近の経費（購入日の降順で先頭数件）
  const recentSorted = React.useMemo(
    () => [...recent].sort((a, b) => (a.purchasedOn < b.purchasedOn ? 1 : -1)),
    [recent],
  );

  const donutData = React.useMemo(
    () =>
      (aggregate?.groups ?? []).map((g) => ({
        label: g.label,
        value: g.totalAmount,
        color: g.color,
      })),
    [aggregate],
  );

  /** 現在の集計期間を引き継いで経費一覧へ遷移する（追加の絞り込み条件も付与可能）。 */
  const goToExpenses = React.useCallback(
    (extra?: Record<string, string>) => {
      const params = new URLSearchParams();
      if (period.dateFrom) params.set('dateFrom', period.dateFrom);
      if (period.dateTo) params.set('dateTo', period.dateTo);
      Object.entries(extra ?? {}).forEach(([k, v]) => {
        if (v) params.set(k, v);
      });
      const qs = params.toString();
      navigate(`/expense/expense-management${qs ? `?${qs}` : ''}`);
    },
    [navigate, period.dateFrom, period.dateTo],
  );

  /** ラベル構成比クリックで経費一覧へドリルダウン（期間 + ラベル） */
  const drilldownLabel = (label: string) => {
    const g = aggregate?.groups.find((x) => x.label === label);
    if (!g) return;
    goToExpenses({ labelIds: g.key });
  };

  const isEmpty = !loading && totalCount === 0 && recent.length === 0;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 1100 }}>
        <DomainHero
          eyebrow="Expense"
          title="Your Money, In Focus"
          tagline="Log what you buy and see where your money goes, from a quick glance to a deep dive."
          palette={HERO_PALETTES.expense}
          onBack={() => navigate('/')}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          actions={
            <Stack direction="column" spacing={1} alignItems="flex-end">
              <Tooltip title="マスタ設定">
                <IconButton
                  onClick={(e) => setSettingsAnchor(e.currentTarget)}
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    border: 1,
                    borderColor: 'rgba(255,255,255,0.35)',
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                  }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={settingsAnchor}
                open={Boolean(settingsAnchor)}
                onClose={() => setSettingsAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      mt: 0.5,
                      minWidth: 200,
                      borderRadius: 2,
                      border: 1,
                      borderColor: 'divider',
                    },
                  },
                }}
              >
                {settingsItems.map((it) => (
                  <MenuItem
                    key={it.key}
                    onClick={() => {
                      setSettingsAnchor(null);
                      navigate(it.to);
                    }}
                  >
                    <ListItemIcon sx={{ color: 'text.secondary' }}>{it.icon}</ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
                      {it.label}
                    </ListItemText>
                  </MenuItem>
                ))}
              </Menu>
              <Link
                component="button"
                variant="body2"
                underline="hover"
                onClick={() => navigate('/expense/expense-dashboard')}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  color: 'rgba(255,255,255,0.95)',
                  fontWeight: 600,
                }}
              >
                もっと詳しく
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Link>
            </Stack>
          }
        />

        {/* 期間セレクタ（共通） */}
        <ExpensePeriodSelector value={state.dateRange} onChange={setDateRange} />

        {/* KPI */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<PaidOutlinedIcon sx={{ fontSize: 16 }} />}
              label="合計金額"
              value={totalAmount.toLocaleString('ja-JP')}
              suffix="円"
              color={ACCENT}
              loading={loading}
              onClick={() => navigate('/expense/expense-dashboard')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<ShoppingCartOutlinedIcon sx={{ fontSize: 16 }} />}
              label="件数"
              value={totalCount}
              suffix="件"
              color="#1565c0"
              loading={loading}
              onClick={() => goToExpenses()}
            />
          </Grid>
        </Grid>

        {isEmpty ? (
          <Widget title="経費">
            <EmptyHint text="まだ経費が登録されていません" />
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Link
                component="button"
                variant="body2"
                underline="hover"
                onClick={() => navigate('/expense/expense-management')}
                sx={{ fontWeight: 600 }}
              >
                経費を登録する
              </Link>
            </Box>
          </Widget>
        ) : (
          <Grid container spacing={2}>
            {/* 最近の経費 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Widget
                title="最近の経費"
                action={
                  <Link
                    component="button"
                    variant="caption"
                    underline="hover"
                    onClick={() => goToExpenses()}
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                  >
                    経費一覧
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Link>
                }
              >
                {loading ? (
                  <Stack spacing={1}>
                    {[0, 1, 2].map((i) => (
                      <Skeleton key={i} variant="rounded" height={44} />
                    ))}
                  </Stack>
                ) : recentSorted.length === 0 ? (
                  <EmptyHint text="対象期間の経費がありません" />
                ) : (
                  <PagedWidgetList
                    items={recentSorted}
                    keyOf={(e) => e.expenseId}
                    pageSize={RECENT_COUNT}
                    page={
                      clampPage(
                        state.recentPage + 1,
                        Math.ceil(recentSorted.length / RECENT_COUNT),
                      ) - 1
                    }
                    onPageChange={setRecentPage}
                    renderItem={(e) => (
                      <WidgetRow accentColor={ACCENT} onClick={() => goToExpenses()}>
                        <ReceiptLongIcon
                          sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {e.name}
                          </Typography>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            sx={{ mt: 0.25 }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(e.purchasedOn)}
                            </Typography>
                            <LabelChip name={e.labelName} color={e.labelColor} size="small" />
                          </Stack>
                        </Box>
                        <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
                          {yen(e.total)}
                        </Typography>
                      </WidgetRow>
                    )}
                  />
                )}
              </Widget>
            </Grid>

            {/* 品目ラベル別構成比 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Widget
                title="品目ラベル別 構成比"
                fit="content"
                action={
                  <Link
                    component="button"
                    variant="caption"
                    underline="hover"
                    onClick={() => navigate('/expense/expense-dashboard')}
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                  >
                    ダッシュボード
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Link>
                }
              >
                {loading ? (
                  <Skeleton variant="rounded" height={260} />
                ) : (
                  <WidgetDonutChart
                    data={donutData}
                    totalLabel="合計"
                    unit="円"
                    onSliceClick={drilldownLabel}
                    emptyText="対象期間の経費がありません"
                  />
                )}
              </Widget>
            </Grid>
          </Grid>
        )}
      </Stack>
    </Box>
  );
};

export default ExpenseHome;
